import React, { useState, useEffect, useMemo, useRef } from "react";
import { useUser } from "@/context/UserContext";
import {
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Button,
  Progress,
  FormControl,
  FormLabel,
  InputGroup,
  InputRightElement,
  Alert,
  AlertIcon,
  Stack,
  Flex,
  Box,
  Text,
  useToast,
  Heading,
  Icon,
  Tooltip,
} from "@chakra-ui/react";
import { FiCalendar, FiShield, FiUsers, FiSave, FiGlobe } from "react-icons/fi";
import { FaArrowLeft, FaArrowRight, FaSignOutAlt } from "react-icons/fa";
import { motion } from "framer-motion";
import GlassButton from "@/components/pressets/GlassButton";
import GlassSelect from "@/components/pressets/GlassSelect";
import GlassInput from "@/components/pressets/GlassInput";
import { updatePersonalDataFields } from "@/api/personalDataService";
import { ScaleFade } from "@chakra-ui/react";
import { BsFillQuestionCircleFill } from "react-icons/bs";
import { clearAuthToken } from "@/lib/authStorage";
import { useRouter } from "next/router";
import { faSignOutAlt } from "@fortawesome/free-solid-svg-icons";
import {
  GlassModalContent,
  GlassModalOverlay,
} from "@/components/ui/GlassModalContent";

const ENUM_OPTIONS = [
  { value: "0", label: "Ninguna (0)" },
  { value: "1", label: "Una (1)" },
  { value: "2", label: "Dos (2)" },
  { value: "3", label: "Tres (3)" },
  { value: "4", label: "Cuatro (4)" },
  { value: "5", label: "Cinco o más (5+)" },
];

const INSURANCE_OPTIONS = [
  { value: 9.45, label: "IESS (9.45%)" },
  { value: 16.1, label: "ISSPOL (16.10%)" },
  { value: 23.0, label: "ISSFA (23%)" },
];

const validateIdentificacion = (v) => {
  if (!v) return false;
  const isRUC = v.length === 13 && v.endsWith("001");
  const isCedula = v.length === 10;
  if (!isRUC && !isCedula) return false;
  const cedula = v.substring(0, 10);
  const provincia = parseInt(cedula.substring(0, 2), 10);
  if (provincia < 1 || provincia > 24) return false;
  const tercerDigito = parseInt(cedula.substring(2, 3), 10);
  if (tercerDigito >= 6) return false;
  const ultimoDigito = parseInt(cedula.substring(9, 10), 10);
  let suma = 0;
  for (let i = 0; i < 9; i++) {
    let valor = parseInt(cedula.charAt(i), 10);
    if (i % 2 === 0) {
      valor = valor * 2;
      if (valor > 9) valor = valor - 9;
    }
    suma += valor;
  }
  const digitoVerificador = suma % 10 === 0 ? 0 : 10 - (suma % 10);
  return digitoVerificador === ultimoDigito;
};

const validateAge = (birthDate) => {
  if (!birthDate) return false;
  const today = new Date();
  const date = new Date(birthDate);
  let age = today.getFullYear() - date.getFullYear();
  const m = today.getMonth() - date.getMonth();
  if (m < 0 || (m === 0 && today.getDate() < date.getDate())) {
    age--;
  }
  return age >= 18;
};

const steps = [
  {
    key: "ruc",
    label: "Identificación",
    icon: FiShield,
    heading: "¿Cuál es tu Identificación? Puede ser RUC o Cédula",
    description: (
      <>
        Este es un dato clave para identificarte ante el SRI. Asegúrate de
        ingresarlo correctamente, <strong>no se podrá modificar después</strong>
        .
      </>
    ),
    render: ({ value, setValue }) => (
      <FormControl id="modal-ruc" isRequired>
        <FormLabel fontSize="sm">Identificación</FormLabel>
        <GlassInput
          value={value}
          onChange={(e) => setValue(e.target.value)}
          placeholder="Introduce tu Identificación (RUC o Cédula)"
          maxLength={13}
        />
      </FormControl>
    ),
    validate: validateIdentificacion,
    error:
      "La identificación debe tener 13 caracteres y terminar en '001' en caso de RUC y 10 caracteres en caso de cédula.",
  },
  {
    key: "birthDate",
    label: "Fecha de Nacimiento",
    icon: FiCalendar,
    heading: "Tu Fecha de Nacimiento",
    description:
      "Este dato es necesario para completar tu perfil y asegurar la correcta validación de tus exoneraciones.",
    tooltip:
      "Las personas de la tercera edad pueden acceder a beneficios fiscales adicionales.",
    render: ({ value, setValue }) => (
      <FormControl id="modal-birthDate" isRequired>
        <FormLabel fontSize="sm">Fecha de Nacimiento</FormLabel>
        <GlassInput
          type="date"
          value={value}
          onChange={(e) => setValue(e.target.value)}
          max={new Date().toISOString().substring(0, 10)}
        />
      </FormControl>
    ),
    validate: validateAge,
    error: "La fecha es requerida y debes ser mayor de edad.",
  },
  {
    key: "familiarCharge",
    label: "Carga Familiar",
    icon: FiUsers,
    tooltip:
      "Una carga familiar es una persona que depende económicamente de ti y no percibe ingresos gravados de impuesto a la renta superiores al salario básico unificado. Pueden ser hijos de hasta 21 años o con discapacidad a cualquier edad, padres, cónyuge o pareja en unión de hecho.",
    heading: "¿Tienes Cargas Familiares?",
    description:
      "Indica el número de personas que tienes como carga familiar. Esto impacta directamente en tus cálculos de deducciones.",
    render: ({ value, setValue }) => (
      <FormControl id="modal-familiarCharge" isRequired>
        <FormLabel fontSize="sm">Número de Cargas Familiares</FormLabel>
        <GlassSelect
          value={value}
          onChange={(e) => setValue(e.target.value)}
          options={ENUM_OPTIONS}
        />
      </FormControl>
    ),
    validate: (v) => v !== undefined && v !== "",
    error: "Selecciona una carga familiar.",
  },
  {
    key: "insurancePercentage",
    label: "Institución de Aportación",
    icon: FiGlobe,
    heading: "Tu Institución de Aportación",
    description:
      "Este valor se usa para calcular correctamente tus deducciones y aportaciones al seguro social (IESS, ISSPOL, etc.).",
    render: ({ value, setValue }) => {
      const selectedOption = INSURANCE_OPTIONS.find(
        (opt) => opt.value === value
      );
      return (
        <FormControl id="modal-insurancePercentage" isRequired>
          <FormLabel fontSize="sm">Institución de Aportación</FormLabel>
          <GlassSelect
            value={value}
            onChange={(e) => setValue(e.target.value)}
            options={INSURANCE_OPTIONS}
          />
          {selectedOption && (
            <Text mt={2} fontSize="xs" color="gray.500" fontStyle="italic">
              {selectedOption.value === "iess" &&
                "Corresponde al Instituto Ecuatoriano de Seguridad Social para trabajadores privados, públicos y autónomos."}
              {selectedOption.value === "isspol" &&
                "Corresponde al Instituto de Seguridad Social de la Policía Nacional."}
              {selectedOption.value === "issfa" &&
                "Corresponde al Instituto de Seguridad Social de las Fuerzas Armadas."}
            </Text>
          )}
        </FormControl>
      );
    },
    validate: (v) => !!v,
    error: "Selecciona un porcentaje.",
  },
  {
    key: "disabilityType",
    label: "Discapacidad o Sustitución",
    icon: FiShield,
    heading: "¿Tienes alguna Discapacidad o Sustitución?",
    description:
      "Selecciona la opción si aplica para ti o si eres sustituto de una persona con discapacidad. Esto puede otorgar beneficios fiscales.",
    render: ({ value, setValue }) => {
      const descriptions = {
        none: "",
        disability:
          "Persona con registro oficial en el Ministerio de Salud o carnet vigente del CONADIS que acredite un grado de discapacidad.",
        substitute:
          "Persona que tiene a su cargo a un familiar con discapacidad y que ha sido registrado legalmente como tal.",
        solidarity:
          "Persona que, sin ser pariente, es responsable legal del cuidado de una persona con discapacidad.",
      };

      return (
        <FormControl id="modal-disabilityType">
          <FormLabel fontSize="sm">Tipo de Discapacidad/Sustitución</FormLabel>
          <GlassSelect
            value={value}
            options={[
              { value: "none", label: "No posee discapacidad" },
              { value: "disability", label: "Posee Discapacidad" },
              { value: "substitute", label: "Sustituto por Discapacidad" },
              { value: "solidarity", label: "Sustituto por Solidaridad" },
            ]}
            onChange={(e) => setValue(e.target.value)}
          />
          {value && (
            <Text mt={2} fontSize="xs" color="gray.500" fontStyle="italic">
              {descriptions[value]}
            </Text>
          )}
        </FormControl>
      );
    },
    validate: (v) => !!v,
    error: "Selecciona un tipo.",
  },
  {
    key: "disabilityPercentage",
    label: "% de Discapacidad",
    icon: FiShield,
    conditional: (values) => values.disabilityType !== "none",
    heading: "Detalles de Discapacidad",
    description:
      "Ingresa el porcentaje de discapacidad según tu carnet CONADIS/MSP.",
    render: ({ value, setValue }) => (
      <FormControl id="modal-disabilityPercentage" isRequired>
        <FormLabel fontSize="sm">% de Discapacidad</FormLabel>
        <GlassInput
          type="number"
          value={value}
          onChange={(e) => setValue(e.target.value)}
          min={0}
          max={100}
          step={0.01}
          placeholder="Ej: 35.5"
        />
      </FormControl>
    ),
    validate: (v) =>
      v !== "" && !isNaN(Number(v)) && Number(v) >= 0 && Number(v) <= 100,
    error: "El porcentaje debe ser entre 0 y 100.",
  },
  {
    key: "disabilityCardNumber",
    label: "N° Carnet CONADIS/MSP",
    icon: FiShield,
    conditional: (values) => values.disabilityType !== "none",
    heading: "Número de Carnet",
    description: "Ingresa el número de tu carnet de discapacidad. (opcional)",
    render: ({ value, setValue }) => (
      <FormControl id="modal-disabilityCardNumber">
        <FormLabel fontSize="sm">N° Carnet CONADIS/MSP</FormLabel>
        <GlassInput
          value={value}
          onChange={(e) => setValue(e.target.value)}
          placeholder="Número de Carnet"
        />
      </FormControl>
    ),
    validate: () => true,
  },
  {
    key: "disabilityCardValidityStartDate",
    label: "Fecha de Vigencia del Carnet",
    icon: FiCalendar,
    conditional: (values) => values.disabilityType !== "none",
    heading: "Vigencia del Carnet",
    description:
      "Ingresa la fecha de inicio de vigencia de tu carnet. (opcional)",
    render: ({ value, setValue }) => (
      <FormControl id="modal-disabilityCardValidityStartDate">
        <FormLabel fontSize="sm">Fecha de Vigencia del Carnet</FormLabel>
        <GlassInput
          type="date"
          value={value}
          onChange={(e) => setValue(e.target.value)}
        />
      </FormControl>
    ),
    validate: () => true,
  },
  {
    key: "disabledPersonId",
    label: "Cédula del Sustituido",
    icon: FiShield,
    conditional: (values) =>
      values.disabilityType === "substitute" ||
      values.disabilityType === "solidarity",
    heading: "Datos del Sustituido",
    description: "Cédula de la persona a la que estás sustituyendo.",
    render: ({ value, setValue }) => (
      <FormControl id="modal-disabledPersonId" isRequired>
        <FormLabel fontSize="sm">Cédula del Sustituido</FormLabel>
        <GlassInput
          value={value}
          onChange={(e) => setValue(e.target.value)}
          placeholder="Número de Identificación"
        />
      </FormControl>
    ),
    validate: (v) => !!v,
    error: "Campo requerido.",
  },
  {
    key: "disabledPersonRelation",
    label: "Relación con Sustituido",
    icon: FiUsers,
    conditional: (values) =>
      values.disabilityType === "substitute" ||
      values.disabilityType === "solidarity",
    heading: "Tu Relación",
    description: "Indica tu parentesco con la persona que sustituyes.",
    render: ({ value, setValue }) => (
      <FormControl id="modal-disabledPersonRelation" isRequired>
        <FormLabel fontSize="sm">Relación con Sustituido</FormLabel>
        <GlassInput
          value={value}
          onChange={(e) => setValue(e.target.value)}
          placeholder="Ej: Hijo, Padre, Cónyuge"
        />
      </FormControl>
    ),
    validate: (v) => !!v,
    error: "Campo requerido.",
  },
  {
    key: "substituteQualificationStartDate",
    label: "Inicio de Calificación",
    icon: FiCalendar,
    conditional: (values) =>
      values.disabilityType === "substitute" ||
      values.disabilityType === "solidarity",
    heading: "Inicio de Calificación como Sustituto",
    description: "Fecha en la que iniciaste tu calificación como sustituto.",
    render: ({ value, setValue }) => (
      <FormControl id="modal-substituteQualificationStartDate" isRequired>
        <FormLabel fontSize="sm">Fecha Inicio Calificación Sustituto</FormLabel>
        <GlassInput
          type="date"
          value={value}
          onChange={(e) => setValue(e.target.value)}
        />
      </FormControl>
    ),
    validate: (v) => !!v,
    error: "Campo requerido.",
  },
];

const MotionModalContent = motion(GlassModalContent);

const DataUpdateModalNoSync = ({
  isOpen,
  onClose,
  initialData = {},
  skipIdentification = false,
}) => {
  const { user } = useUser();

  const [step, setStep] = useState(0);
  const [values, setValues] = useState(() => ({
    ruc: initialData.ruc || "",
    birthDate: initialData.birthDate
      ? new Date(initialData.birthDate).toISOString().substring(0, 10)
      : "",
    familiarCharge: initialData.familiarCharge
      ? initialData.familiarCharge[new Date().getFullYear()]?.toString() ?? "0"
      : "0",
    insurancePercentage: initialData.insurancePercentage?.toString() || "9.45",
    disabilityType:
      !initialData.disabilityData?.hasDisability &&
      !initialData.disabilityData?.disabilitySubstitute &&
      !initialData.disabilityData?.solidarityDisabilitySubstitute
        ? "none"
        : initialData.disabilityData?.hasDisability
        ? "disability"
        : initialData.disabilityData?.disabilitySubstitute
        ? "substitute"
        : initialData.disabilityData?.solidarityDisabilitySubstitute
        ? "solidarity"
        : "none",
    disabilityPercentage:
      initialData.disabilityData?.disabilityPercentage?.toString() || "",
    disabilityCardNumber:
      initialData.disabilityData?.disabilityCardNumber || "",
    disabilityCardValidityStartDate: initialData.disabilityData
      ?.disabilityCardValidityStartDate
      ? new Date(initialData.disabilityData.disabilityCardValidityStartDate)
          .toISOString()
          .substring(0, 10)
      : "",
    disabledPersonId: initialData.disabilityData?.disabledPersonId || "",
    disabledPersonRelation:
      initialData.disabilityData?.disabledPersonRelation || "",
    substituteQualificationStartDate: initialData.disabilityData
      ?.substituteQualificationStartDate
      ? new Date(initialData.disabilityData.substituteQualificationStartDate)
          .toISOString()
          .substring(0, 10)
      : "",
  }));
  const [error, setError] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const toast = useToast();
  const initialRef = useRef();
  const { router } = useRouter();

  const filteredSteps = useMemo(
    () =>
      steps.filter(
        (s) =>
          (!skipIdentification || s.key !== "ruc") &&
          (!s.conditional || s.conditional(values))
      ),
    [values, skipIdentification]
  );

  const currentStep = filteredSteps[step];

  const handleChange = (key, val) => {
    setValues((v) => ({ ...v, [key]: val }));
  };

  useEffect(() => {
    // console.log("values:", values);
  }, [values]);

  const handleNext = () => {
    setError("");
    if (
      currentStep.validate &&
      !currentStep.validate(values[currentStep.key])
    ) {
      setError(currentStep.error || "Campo requerido.");
      return;
    }
    setStep((s) => s + 1);
  };

  const handlePrev = () => {
    setError("");
    setStep((s) => (s > 0 ? s - 1 : 0));
  };

  const handleSubmit = async () => {
    setError("");
    if (
      currentStep.validate &&
      !currentStep.validate(values[currentStep.key])
    ) {
      setError(currentStep.error || "Campo requerido.");
      return;
    }

    setIsSubmitting(true);
    try {
      const currentYear = new Date().getFullYear().toString();
      const familiarChargeValue = values.familiarCharge
        ? parseInt(values.familiarCharge)
        : 0;

      const payload = {
        syncType: "MANUAL", // Siempre MANUAL
        birthDate: values.birthDate
          ? new Date(values.birthDate).toISOString()
          : null,
        // Dotted key preserves other years on established profiles.
        [`familiarCharge.${currentYear}`]: familiarChargeValue,
        insurancePercentage: values.insurancePercentage
          ? parseFloat(values.insurancePercentage)
          : 9.45,
        disabilityData: {
          hasDisability: values.disabilityType === "disability",
          disabilityPercentage:
            values.disabilityType === "none"
              ? null
              : values.disabilityPercentage
              ? Number(values.disabilityPercentage)
              : null,
          disabilitySubstitute: values.disabilityType === "substitute",
          solidarityDisabilitySubstitute:
            values.disabilityType === "solidarity",
          disabledPersonId:
            values.disabilityType === "substitute" ||
            values.disabilityType === "solidarity"
              ? values.disabledPersonId
              : null,
          disabledPersonRelation:
            values.disabilityType === "substitute" ||
            values.disabilityType === "solidarity"
              ? values.disabledPersonRelation
              : null,
          substituteQualificationStartDate:
            (values.disabilityType === "substitute" ||
              values.disabilityType === "solidarity") &&
            values.substituteQualificationStartDate
              ? new Date(values.substituteQualificationStartDate).toISOString()
              : null,
          disabilityCardNumber:
            values.disabilityType === "none"
              ? null
              : values.disabilityCardNumber,
          disabilityCardValidityStartDate:
            values.disabilityType === "none"
              ? null
              : values.disabilityCardValidityStartDate
              ? new Date(values.disabilityCardValidityStartDate).toISOString()
              : null,
        },
      };
      await updatePersonalDataFields(payload);

      toast({
        title: "Datos actualizados",
        description: "Tus datos personales han sido actualizados exitosamente.",
        status: "success",
        duration: 4000,
        isClosable: true,
      });
      onClose();
    } catch (e) {
      toast({
        title: "Error al actualizar",
        description:
          e.response?.data?.message || "Ocurrió un error en el servidor.",
        status: "error",
        duration: 4000,
        isClosable: true,
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  useEffect(() => {
    if (!isOpen) {
      setStep(0);
      setError("");
    }
  }, [isOpen]);

  const welcomeMessage = `¡Hola, ${
    user?.display_name || ""
  }! Empecemos con tu configuración inicial en ConTex.`;

  return (
    <>
      {isOpen && (
        <GlassButton
          leftIcon={<FaSignOutAlt />}
          colorScheme="red"
          onClick={() => {
            clearAuthToken();
          }}
          position="fixed"
          bottom="24px"
          right="24px"
          w="auto"
          h="48px"
          px={6}
          py={3}
          fontWeight="medium"
          zIndex={100000}
          boxShadow="xl"
        >
          Cerrar sesión
        </GlassButton>
      )}
      <Modal
        isOpen={isOpen}
        onClose={onClose}
        initialFocusRef={initialRef}
        size="lg"
        isCentered
        motionPreset="scale"
        closeOnOverlayClick={false}
        closeOnEsc={false}
      >
        <GlassModalOverlay />
        <MotionModalContent
          initial={{ scale: 0.95, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          exit={{ scale: 0.95, opacity: 0 }}
          transition={{ duration: 0.2 }}
          rounded="xl"
          shadow="2xl"
        >
          <ModalHeader pb={2} pt={7} px={8}>
            <Flex direction="column" align="flex-start" justify="center">
              <Heading size="md" mb={6} color="brand.600" textAlign="center">
                {welcomeMessage}
              </Heading>
              <Flex align="center">
                <Box
                  as={currentStep.icon}
                  boxSize="22px"
                  color="gray.600"
                  mr={3}
                />
                <Text fontWeight="bold" fontSize="md" color="gray.600">
                  Paso {step + 1} de {filteredSteps.length}: {currentStep.label}
                </Text>
              </Flex>
            </Flex>
          </ModalHeader>

          <Progress
            value={((step + 1) / filteredSteps.length) * 100}
            size="xs"
            colorScheme="green"
            rounded="full"
            mx={8}
            mb={4}
          />

          <ModalBody px={8} py={5}>
            <ScaleFade in={true} initialScale={0.95}>
              <Stack spacing={6}>
                <Box>
                  <Heading size="sm" color="gray.700" mb={2}>
                    {currentStep.heading}
                  </Heading>
                  <Text fontSize="sm" color="gray.500">
                    {currentStep.description}
                    {currentStep.tooltip && (
                      <Box display="inline-block" ml={2}>
                        <Tooltip
                          label={currentStep.tooltip}
                          placement="right"
                          hasArrow
                        >
                          <Button
                            size="xs"
                            variant="ghost"
                            colorScheme="green"
                            p={0}
                            minW="unset"
                            h="1.5rem"
                            w="1.5rem"
                            borderRadius="full"
                            aria-label="Información"
                          >
                            <Icon as={BsFillQuestionCircleFill} boxSize={4} />
                          </Button>
                        </Tooltip>
                      </Box>
                    )}
                  </Text>
                </Box>
                {currentStep.render({
                  value: values[currentStep.key],
                  values,
                  setValue: (v) => handleChange(currentStep.key, v),
                  handleChange,
                })}
                {error && (
                  <Alert variant="glass" status="error" rounded="lg" fontSize="sm">
                    <AlertIcon />
                    {error}
                  </Alert>
                )}
              </Stack>
            </ScaleFade>
          </ModalBody>
          <ModalFooter px={8} pt={4} pb={7}>
            <Flex w="full" justify="space-between">
              <Button
                onClick={handlePrev}
                isDisabled={step === 0 || isSubmitting}
                variant="ghost"
                leftIcon={<FaArrowLeft />}
                color="gray.600"
                size="lg"
              >
                Atrás
              </Button>
              {step < filteredSteps.length - 1 ? (
                <GlassButton
                  onClick={handleNext}
                  colorScheme="green"
                  rightIcon={<FaArrowRight />}
                  isDisabled={isSubmitting}
                  size="lg"
                  py={6}
                >
                  Siguiente
                </GlassButton>
              ) : (
                <GlassButton
                  onClick={handleSubmit}
                  colorScheme="green"
                  isLoading={isSubmitting}
                  leftIcon={<FiSave />}
                  size="lg"
                  py={6}
                >
                  Finalizar y Guardar
                </GlassButton>
              )}
            </Flex>
          </ModalFooter>
        </MotionModalContent>
      </Modal>
    </>
  );
};

export default DataUpdateModalNoSync;
